// SessionManager.js
// A singleton service to manage session expiry modal from outside React

class SessionManagerClass {
  constructor() {
    this.listeners = [];
    this.isSessionExpired = false;
    this.pendingRequest = null;
  }

  // Subscribe to session events
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(
        (listener) => listener !== callback
      );
    };
  }

  // Notify all listeners
  notify(event) {
    this.listeners.forEach((listener) => listener(event));
  }

  // Show session expired modal
  showSessionExpiredModal(originalRequest) {
    if (this.isSessionExpired) {
      // Already showing modal, queue this request
      return new Promise((resolve) => {
        this.pendingRequest = { resolve, originalRequest };
      });
    }

    this.isSessionExpired = true;

    return new Promise((resolve) => {
      this.pendingRequest = { resolve, originalRequest };
      this.notify({
        type: "SESSION_EXPIRED",
        show: true,
      });
    });
  }

  // Handle continue (refresh token)
  handleContinue(newToken) {
    this.isSessionExpired = false;

    if (this.pendingRequest) {
      const { resolve, originalRequest } = this.pendingRequest;

      // Update token on retried request
      if (originalRequest && originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
      }

      // Resolve with retry instruction
      resolve({
        retry: true,
        cancelled: false,
        invalid: false,
        originalRequest,
      });

      this.pendingRequest = null;
    }

    this.notify({
      type: "SESSION_REFRESHED",
      show: false,
    });
  }

  // Handle cancel (logout)
  handleCancel() {
    this.isSessionExpired = false;

    if (this.pendingRequest) {
      const { resolve } = this.pendingRequest;

      resolve({
        retry: false,
        cancelled: true,
        invalid: false,
        message: "Session expired - user cancelled",
      });

      this.pendingRequest = null;
    }

    this.notify({
      type: "SESSION_CANCELLED",
      show: false,
    });
  }

  // Handle session invalid (maybe refresh token failed)
  handleSessionInvalid() {
    this.isSessionExpired = false;

    if (this.pendingRequest) {
      const { resolve } = this.pendingRequest;

      resolve({
        retry: false,
        cancelled: false,
        invalid: true,
        message: "Session invalid - please login",
      });

      this.pendingRequest = null;
    }

    this.notify({
      type: "SESSION_INVALID",
      show: false,
    });
  }

  // Reset state
  reset() {
    this.isSessionExpired = false;
    this.pendingRequest = null;
  }
}

const SessionManager = new SessionManagerClass();
export default SessionManager;
